import { render, $, statusBar, cardDisplay, logEntry, delay } from './components';
import { showCardDetail } from './CardDetailModal';
import { renderBattleField, playBattleAnimation, injectBattleAnimationStyles } from './BattleAnimation';
import { MatchState, MatchResult, BattleLogEntry, CardInstance } from '../types';
import { getCardById } from '../cards';

export interface MatchPhaseCallbacks {
  onMatchComplete: (result: MatchResultSummary) => void;
}

// 勝敗結果サマリー
export interface MatchResultSummary {
  isWinner: boolean;
  fansGained: number;
  fansLost: number;
  flagsWon: number;
  benchCards: number;
  streak: number; // 正: 連勝, 負: 連敗
  isEliminated: boolean;
}

export async function renderMatchPhase(
  matchState: MatchState,
  myPlayerId: string,
  matchResult: MatchResult | null,
  callbacks: MatchPhaseCallbacks,
  additionalInfo?: {
    currentFans: number;
    streak: number;
    isEliminated: boolean;
  }
): Promise<void> {
  // アニメーションスタイル注入
  injectBattleAnimationStyles();

  const myIdx = matchState.players.findIndex((p) => p.playerId === myPlayerId);
  const opponentIdx = myIdx === 0 ? 1 : 0;

  const me = matchState.players[myIdx] ?? matchState.players[0];
  const opponent = matchState.players[opponentIdx] ?? matchState.players[1];

  render(`
    <div class="match-phase" style="padding: 16px; max-width: 1000px; margin: 0 auto;">
      <div style="text-align: center; margin-bottom: 16px;">
        <h1 style="
          color: #ff6b6b;
          text-shadow: 0 0 30px #ff6b6b;
          font-size: clamp(1.8rem, 6vw, 2.5rem);
          letter-spacing: 4px;
          animation: title-pulse 2s ease-in-out infinite;
        ">⚔️ BATTLE ⚔️</h1>
      </div>

      <!-- バトルフィールド -->
      ${renderBattleField(matchState, myPlayerId)}

      <!-- バトルログ -->
      <div style="
        background: rgba(0,0,0,0.4);
        border-radius: 12px;
        padding: 12px;
        max-height: 180px;
        overflow-y: auto;
        border: 1px solid #333;
      ">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <h3 style="color: #00fff5; margin: 0; font-size: 0.95rem;">📜 Battle Log</h3>
          <button id="btnSkipAnimation" style="
            background: transparent;
            border: 1px solid #666;
            color: #aaa;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 0.75rem;
            cursor: pointer;
            min-height: 32px;
          ">スキップ ⏭</button>
        </div>
        <div id="battleLog" style="font-size: 0.8rem;"></div>
      </div>
    </div>
  `);

  // タイトルアニメーションCSS追加
  addTitleAnimation();

  // 結果サマリー計算
  const isWinner = matchState.winner === myPlayerId;
  const fansGained = isWinner && matchResult ? matchResult.winnerFansGained : 0;
  const fansLost = !isWinner ? 1 : 0; // TournamentManagerのcalculateFansLostに合わせる
  const flagsWon = matchResult?.winnerFlags ?? 0;
  const benchCards = me.field.bench.length;
  
  const resultSummary: MatchResultSummary = {
    isWinner,
    fansGained,
    fansLost,
    flagsWon: isWinner ? flagsWon : 0,
    benchCards,
    streak: additionalInfo?.streak ?? 0,
    isEliminated: additionalInfo?.isEliminated ?? false,
  };

  // スキップボタン
  let skipAnimation = false;
  document.getElementById('btnSkipAnimation')?.addEventListener('click', () => {
    skipAnimation = true;
    showAllLogs(matchState.log, myPlayerId);
  });

  // アニメーション再生
  await playBattleAnimation(matchState, myPlayerId, () => {
    if (!skipAnimation) {
      // アニメーション完了後に詳細結果表示
      showDetailedMatchResult(matchState, myPlayerId, resultSummary, additionalInfo?.currentFans ?? 0, () => {
        callbacks.onMatchComplete(resultSummary);
      });
    }
  });

  // スキップされた場合
  if (skipAnimation) {
    showDetailedMatchResult(matchState, myPlayerId, resultSummary, additionalInfo?.currentFans ?? 0, () => {
      callbacks.onMatchComplete(resultSummary);
    });
  }
}

// 全ログを即座に表示
function showAllLogs(logs: BattleLogEntry[], myPlayerId: string): void {
  const logEl = document.getElementById('battleLog');
  if (!logEl) return;

  logEl.innerHTML = logs.map((entry) => {
    const isMe = entry.playerId === myPlayerId;
    const prefix = isMe ? '👤' : '🤖';
    
    let type: 'normal' | 'attack' | 'ability' | 'result' = 'normal';
    if (entry.action === 'reveal' || entry.action === 'flagTaken') type = 'attack';
    if (entry.action === 'abilityTrigger') type = 'ability';
    if (entry.action === 'matchEnd') type = 'result';

    return logEntry(`${prefix} ${entry.message}`, type);
  }).join('');
}

// タイトルアニメーション追加
function addTitleAnimation(): void {
  if (document.getElementById('match-title-anim')) return;
  
  const style = document.createElement('style');
  style.id = 'match-title-anim';
  style.textContent = `
    @keyframes title-pulse {
      0%, 100% { 
        text-shadow: 0 0 30px #ff6b6b;
        transform: scale(1);
      }
      50% { 
        text-shadow: 0 0 50px #ff6b6b, 0 0 80px #ff6b6b;
        transform: scale(1.02);
      }
    }
  `;
  document.head.appendChild(style);
}

// 詳細なマッチ結果表示
function showDetailedMatchResult(
  matchState: MatchState, 
  myPlayerId: string, 
  summary: MatchResultSummary,
  currentFans: number,
  onComplete: () => void
): void {
  const isWinner = summary.isWinner;
  const newFans = currentFans + (isWinner ? summary.fansGained : -summary.fansLost);
  
  // ストリーク表示
  const streakText = getStreakText(summary.streak);
  
  const overlay = document.createElement('div');
  overlay.id = 'matchResultOverlay';
  overlay.innerHTML = `
    <div style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.5s ease-out;
      padding: 20px;
    ">
      <div style="
        text-align: center;
        max-width: 400px;
        width: 100%;
        background: linear-gradient(135deg, rgba(30,30,50,0.95) 0%, rgba(20,20,40,0.98) 100%);
        border-radius: 20px;
        padding: 24px;
        border: 2px solid ${isWinner ? '#ffd700' : '#666'};
        box-shadow: 0 0 40px ${isWinner ? 'rgba(255,215,0,0.3)' : 'rgba(0,0,0,0.5)'};
      ">
        <!-- 勝敗タイトル -->
        <div style="
          font-size: clamp(2rem, 8vw, 3rem);
          color: ${isWinner ? '#ffd700' : '#888'};
          text-shadow: 0 0 30px ${isWinner ? '#ffd700' : '#333'};
          animation: ${isWinner ? 'bounce 0.5s ease-out' : 'none'};
          margin-bottom: 16px;
        ">
          ${isWinner ? '🏆 VICTORY!' : '💀 DEFEAT'}
        </div>
        
        ${summary.isEliminated ? `
          <div style="
            background: rgba(231,76,60,0.2);
            border: 1px solid #e74c3c;
            color: #e74c3c;
            padding: 10px;
            border-radius: 8px;
            margin-bottom: 16px;
            font-size: 0.9rem;
          ">
            ⚠️ 敗退しました
          </div>
        ` : ''}
        
        <!-- ストリーク表示 -->
        ${streakText ? `
          <div style="
            color: ${summary.streak > 0 ? '#2ecc71' : '#e74c3c'};
            font-size: 1rem;
            margin-bottom: 12px;
          ">
            ${streakText}
          </div>
        ` : ''}
        
        <!-- スタッツ -->
        <div style="
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin: 16px 0;
        ">
          <!-- ファン変動 -->
          <div style="
            background: rgba(0,0,0,0.3);
            border-radius: 10px;
            padding: 12px;
          ">
            <div style="color: #888; font-size: 0.75rem; margin-bottom: 4px;">ファン</div>
            <div style="
              color: ${isWinner ? '#2ecc71' : '#e74c3c'};
              font-size: 1.5rem;
              font-weight: bold;
            ">
              ${isWinner ? '+' : '-'}${isWinner ? summary.fansGained : summary.fansLost}
            </div>
            <div style="color: #ffd700; font-size: 0.8rem; margin-top: 4px;">
              ★ ${newFans}
            </div>
          </div>
          
          <!-- フラッグ/ベンチ -->
          <div style="
            background: rgba(0,0,0,0.3);
            border-radius: 10px;
            padding: 12px;
          ">
            <div style="color: #888; font-size: 0.75rem; margin-bottom: 4px;">
              ${isWinner ? 'フラッグ獲得' : 'ベンチ退場'}
            </div>
            <div style="
              color: ${isWinner ? '#00fff5' : '#ff6b6b'};
              font-size: 1.5rem;
              font-weight: bold;
            ">
              ${isWinner ? `🚩 ${summary.flagsWon}` : `📥 ${summary.benchCards}`}
            </div>
          </div>
        </div>
        
        <!-- 続けるボタン -->
        <button id="btnContinue" style="
          background: linear-gradient(135deg, #00fff5 0%, #00bcd4 100%);
          border: none;
          color: #1a1a2e;
          padding: 12px 32px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: bold;
          cursor: pointer;
          min-height: 48px;
          width: 100%;
          margin-top: 8px;
        ">続ける</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  document.getElementById('btnContinue')?.addEventListener('click', () => {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.3s';
    setTimeout(() => {
      overlay.remove();
      onComplete();
    }, 300);
  });
}

// ストリークテキスト生成
function getStreakText(streak: number): string {
  if (streak >= 5) return `🔥🔥🔥 ${streak}連勝! ON FIRE!`;
  if (streak >= 3) return `🔥 ${streak}連勝中!`;
  if (streak >= 2) return `${streak}連勝中`;
  if (streak <= -3) return `😰 ${Math.abs(streak)}連敗中...`;
  if (streak <= -2) return `${Math.abs(streak)}連敗中`;
  return '';
}

// シンプルな結果表示 (後方互換性)
function showMatchResult(matchState: MatchState, myPlayerId: string): void {
  const isWinner = matchState.winner === myPlayerId;
  
  const overlay = document.createElement('div');
  overlay.innerHTML = `
    <div style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.5s ease-out;
    ">
      <div style="text-align: center;">
        <div style="
          font-size: 4rem;
          color: ${isWinner ? '#ffd700' : '#666'};
          text-shadow: 0 0 40px ${isWinner ? '#ffd700' : '#333'};
          animation: ${isWinner ? 'bounce' : 'none'} 0.5s ease-out;
        ">
          ${isWinner ? '🏆 VICTORY!' : '💀 DEFEAT...'}
        </div>
        <div style="color: #aaa; margin-top: 16px; font-size: 1.2rem;">
          ${isWinner ? 'ファンを獲得!' : '次は勝とう!'}
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  setTimeout(() => {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.5s';
    setTimeout(() => overlay.remove(), 500);
  }, 2500);
}

// アニメーション用CSS
const matchStyle = document.createElement('style');
matchStyle.textContent = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes bounce {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }
  .bench-card:hover {
    transform: scale(1.2);
    z-index: 10;
  }
`;
document.head.appendChild(matchStyle);
